import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { fetchNotifications, markAllAsRead as apiMarkAllRead, markAsRead as apiMarkRead, deleteNotification as apiDeleteNotification } from "../services/notificationService";
import { db } from "../firebase";
import { collection, query, orderBy, limit, onSnapshot as firestoreOnSnapshot } from "firebase/firestore";

const NotificationContext = createContext();

const POLL_INTERVAL = 15000;

const typeConfig = {
  course: { icon: "mdi mdi-book-open-page-variant", color: "#4f46e5", bg: "#eef2ff", label: "Courses", path: "/course-list" },
  placement: { icon: "mdi mdi-briefcase", color: "#059669", bg: "#ecfdf5", label: "Placements", path: "/job-list" },
  partner: { icon: "mdi mdi-handshake", color: "#d97706", bg: "#fffbeb", label: "Partners", path: "/partner-list" },
  issue: { icon: "mdi mdi-alert-circle", color: "#dc2626", bg: "#fef2f2", label: "Issues", path: "/IssueList" },
  user: { icon: "mdi mdi-account-plus", color: "#0891b2", bg: "#ecfeff", label: "Users", path: "/user-list" },
  general: { icon: "mdi mdi-bell", color: "#6b7280", bg: "#f3f4f6", label: "General", path: null },
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const intervalRef = useRef(null);
  const unsubRef = useRef(null);
  const isAuthenticated = !!localStorage.getItem("token");

  const load = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      setLoading(true);
      const res = await fetchNotifications({ limit: 20 });
      if (res.success) {
        setNotifications(res.data || []);
        setUnreadCount(res.unreadCount || 0);
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    load();
    intervalRef.current = setInterval(load, POLL_INTERVAL);
    try {
      const q = query(
        collection(db, "notifications"),
        orderBy("createdAt", "desc"),
        limit(20)
      );
      let isFirstSnapshot = true;
      const unsub = firestoreOnSnapshot(
        q,
        (snapshot) => {
          if (isFirstSnapshot) {
            isFirstSnapshot = false;
            return;
          }
          snapshot.docChanges().forEach((change) => {
            if (change.type === 'added') {
              const data = change.doc.data();
              const notif = {
                id: change.doc.id,
                type: data.type || "general",
                title: data.title || "",
                message: data.message || "",
                referenceId: data.referenceId || "",
                isRead: data.isRead || false,
                createdAt: data.createdAt?.toDate ? data.createdAt.toDate().toISOString() : data.createdAt || new Date().toISOString(),
              };
              setNotifications((prev) => [notif, ...prev]);
              if (!notif.isRead) {
                setUnreadCount((prev) => prev + 1);
              }
            } else if (change.type === 'modified') {
              const data = change.doc.data();
              setNotifications((prev) =>
                prev.map((n) =>
                  n.id === change.doc.id
                    ? { ...n, isRead: data.isRead || false, title: data.title || n.title, message: data.message || n.message }
                    : n
                )
              );
            }
          });
        },
        () => {}
      );
      unsubRef.current = unsub;
    } catch (e) {
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (unsubRef.current) {
        unsubRef.current();
        unsubRef.current = null;
      }
    };
  }, [isAuthenticated, load]);

  const getTypeConfig = useCallback((type) => {
    return typeConfig[type] || typeConfig.general;
  }, []);

  const getRelativeTime = useCallback((dateString) => {
    const diff = Math.floor((Date.now() - new Date(dateString)) / 1000);
    if (diff < 60) return "Just now";
    if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
    if (diff < 172800) return "Yesterday";
    return `${Math.floor(diff / 86400)} days ago`;
  }, []);

  const markAsRead = useCallback(async (id) => {
    try {
      await apiMarkRead(id);
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, isRead: true } : n)));
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {}
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      await apiMarkAllRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (err) {}
  }, []);

  const removeNotification = useCallback(async (id) => {
    try {
      await apiDeleteNotification(id);
      setNotifications((prev) => {
        const removed = prev.find((n) => n.id === id);
        if (removed && !removed.isRead) setUnreadCount((c) => Math.max(0, c - 1));
        return prev.filter((n) => n.id !== id);
      });
    } catch (err) {}
  }, []);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        loading,
        getTypeConfig,
        getRelativeTime,
        markAsRead,
        markAllRead,
        removeNotification,
        refresh: load,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error("useNotifications must be used within NotificationProvider");
  return ctx;
};

export { typeConfig };